/**
 * Raw Material Deliveries Page Component
 * Displays paginated list of raw material deliveries with filters and summary
 *
 * @author SAUBHAGYA Development Team
 * @since Raw Material Deliveries Module
 *
 * @description
 * Features:
 * - Summary cards (Total, Active, In Progress, Completed)
 * - Filters (Status, Driver search, Route search, Date range)
 * - Data table with delivery list
 * - Click to view details in modal
 * - Pagination controls
 * - Loading and empty states
 */

import React, { useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Truck,
  Search,
  X,
  RefreshCw,
  MapPin,
  Clock,
  CheckCircle2,
  User,
  Filter,
  Eye,
} from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

import { useScheduledDeliveries } from '@/hooks/useRawMaterialDeliveries';
import { DeliveryStatusBadge, DeliveryStatus } from '@/components/manufacturing/badges/DeliveryStatusBadge';
import { DeliveryDetailsModal } from '@/components/manufacturing/modals/DeliveryDetailsModal';
import { RawMaterialDeliveryDTO } from '@/services/manufacturing/rawMaterialDeliveryApi';

/**
 * Status options for filter
 */
const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'PAUSED', label: 'Paused' },
];

/**
 * Summary Card Component
 */
interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  description?: string;
  isLoading?: boolean;
}

function SummaryCard({ title, value, icon, description, isLoading }: SummaryCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-8 w-24" />
        ) : (
          <>
            <div className="text-2xl font-bold">{value}</div>
            {description && (
              <p className="text-xs text-muted-foreground mt-1">{description}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Table Loading Skeleton
 */
function TableSkeleton() {
  return (
    <>
      {[...Array(5)].map((_, i) => (
        <TableRow key={i}>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
        </TableRow>
      ))}
    </>
  );
}

/**
 * Raw Material Deliveries Page Component
 */
export function RawMaterialDeliveriesPage() {
  // State for filters (client-side filtering)
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [routeSearch, setRouteSearch] = useState<string>('');
  const [driverSearch, setDriverSearch] = useState<string>('');

  // State for filter panel
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // State for selected delivery (for modal) - stores the full delivery object
  const [selectedDelivery, setSelectedDelivery] = useState<RawMaterialDeliveryDTO | null>(null);

  // State for refresh animation
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Fetch scheduled deliveries (active routes) - uses existing working API
  const { data: scheduledDeliveries, isLoading: isLoadingScheduled, refetch: refetchScheduled } = useScheduledDeliveries();


  // Apply client-side filtering
  const filteredDeliveries = useMemo(() => {
    let deliveries = scheduledDeliveries || [];

    // Filter by status
    if (statusFilter && statusFilter !== 'ALL') {
      deliveries = deliveries.filter(d => d.status === statusFilter);
    }

    // Filter by route search
    if (routeSearch) {
      const search = routeSearch.toLowerCase();
      deliveries = deliveries.filter(d =>
        d.routeCode?.toLowerCase().includes(search) ||
        d.routeName?.toLowerCase().includes(search)
      );
    }

    // Filter by driver search
    if (driverSearch) {
      const search = driverSearch.toLowerCase();
      deliveries = deliveries.filter(d =>
        d.driverName?.toLowerCase().includes(search)
      );
    }

    return deliveries;
  }, [scheduledDeliveries, statusFilter, routeSearch, driverSearch]);

  // Calculate stats from all scheduled deliveries (not filtered)
  const stats = useMemo(() => {
    const deliveries = scheduledDeliveries || [];
    return {
      total: deliveries.length,
      active: deliveries.filter(d => d.status === 'ACTIVE').length,
      inProgress: deliveries.filter(d => d.status === 'IN_PROGRESS').length,
      completed: deliveries.filter(d => d.status === 'COMPLETED').length,
    };
  }, [scheduledDeliveries]);

  // Check if any filters are active
  const hasFilters = !!(
    (statusFilter && statusFilter !== 'ALL') ||
    driverSearch ||
    routeSearch
  );

  /**
   * Handle filter changes
   */
  function handleStatusChange(value: string) {
    setStatusFilter(value);
  }

  function handleDriverSearchChange(value: string) {
    setDriverSearch(value);
  }

  function handleRouteSearchChange(value: string) {
    setRouteSearch(value);
  }

  function handleClearFilters() {
    setStatusFilter('ALL');
    setDriverSearch('');
    setRouteSearch('');
  }

  /**
   * Refresh the Delivery List from the transport service.
   */
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const scheduledResult = await refetchScheduled();
      if (scheduledResult.status === 'error') {
        toast.error('Failed to refresh data', {
          description: 'Some data could not be updated. Please try again.',
        });
      } else {
        toast.success('Data refreshed successfully', {
          description: 'Delivery list has been updated.',
        });
      }
    } catch {
      toast.error('Failed to refresh data', {
        description: 'Please check your connection and try again.',
      });
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, refetchScheduled]);

  // Use filtered deliveries for display
  const displayData = filteredDeliveries;

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Raw Material Deliveries</h1>
          <p className="text-muted-foreground">
            View and track incoming raw material deliveries from gaushalas
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="shrink-0"
          aria-label="Refresh deliveries"
          tabIndex={0}
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          title="Total Deliveries"
          value={stats.total}
          icon={<Truck className="h-4 w-4 text-muted-foreground" />}
          description="All scheduled routes"
          isLoading={isLoadingScheduled}
        />
        <SummaryCard
          title="Active"
          value={stats.active}
          icon={<Clock className="h-4 w-4 text-blue-600" />}
          description="Ready for pickup"
          isLoading={isLoadingScheduled}
        />
        <SummaryCard
          title="In Progress"
          value={stats.inProgress}
          icon={<MapPin className="h-4 w-4 text-purple-600" />}
          description="Currently on route"
          isLoading={isLoadingScheduled}
        />
        <SummaryCard
          title="Completed"
          value={stats.completed}
          icon={<CheckCircle2 className="h-4 w-4 text-green-600" />}
          description="Delivered today"
          isLoading={isLoadingScheduled}
        />
      </div>

      {/* Filters */}
      <Collapsible open={isFiltersOpen} onOpenChange={setIsFiltersOpen}>
        <Card>
          <CardHeader className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base font-medium">Filters</CardTitle>
                {hasFilters && (
                  <Badge variant="secondary" className="ml-2">
                    Active
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {hasFilters && (
                  <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm">
                    {isFiltersOpen ? 'Hide' : 'Show'} Filters
                  </Button>
                </CollapsibleTrigger>
              </div>
            </div>
          </CardHeader>
          <CollapsibleContent>
            <CardContent className="pt-0">
              <div className="grid gap-4 md:grid-cols-3">
                {/* Status Filter */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Status</label>
                  <Select
                    value={statusFilter}
                    onValueChange={handleStatusChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Route Search */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Route</label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search route..."
                      value={routeSearch}
                      onChange={(e) => handleRouteSearchChange(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>

                {/* Driver Search */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Driver</label>
                  <div className="relative">
                    <User className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search driver..."
                      value={driverSearch}
                      onChange={(e) => handleDriverSearchChange(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Deliveries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Delivery List
            {displayData.length > 0 && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                ({displayData.length} total)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Route ID</TableHead>
                  <TableHead>Route Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Stops</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoadingScheduled && <TableSkeleton />}

                {!isLoadingScheduled && displayData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="h-24 text-center">
                      <div className="flex flex-col items-center justify-center text-muted-foreground">
                        <Truck className="h-8 w-8 mb-2" />
                        <p>No deliveries found</p>
                        <p className="text-sm">No scheduled routes at this time</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}

                {!isLoadingScheduled &&
                  displayData.map((delivery) => (
                    <TableRow
                      key={delivery.id || delivery.routeId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setSelectedDelivery(delivery)}
                    >
                      <TableCell className="font-medium">
                        {delivery.routeCode}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {delivery.routeName}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DeliveryStatusBadge
                          status={delivery.status as DeliveryStatus}
                          statusName={delivery.statusName}
                          size="sm"
                          showPulse={delivery.status === 'ACTIVE' || delivery.status === 'IN_PROGRESS'}
                        />
                      </TableCell>
                      <TableCell>
                        {delivery.driverName ? (
                          <div className="flex items-center gap-1">
                            <User className="h-3 w-3 text-muted-foreground" />
                            <span>{delivery.driverName}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Not assigned</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {delivery.vehicleNumber ? (
                          <div className="flex items-center gap-1">
                            <Truck className="h-3 w-3 text-muted-foreground" />
                            <span>{delivery.vehicleNumber}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {delivery.totalStops != null ? (
                          <div className="flex items-center gap-1">
                            <MapPin className="h-3 w-3 text-muted-foreground" />
                            <span>{delivery.totalStops}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* Driver Progress Column */}
                        {delivery.todayExecutionId ? (
                          delivery.todayExecutionStatus === 'COMPLETED' ? (
                            <div className="flex items-center gap-1 text-gray-600">
                              <CheckCircle2 className="h-3 w-3 text-gray-500" />
                              <span className="text-xs">Completed</span>
                            </div>
                          ) : delivery.currentStopIndex && delivery.totalStops ? (
                            <div className="flex items-center gap-1">
                              <MapPin className="h-3 w-3 text-blue-500 animate-pulse" />
                              <span className="text-xs font-medium text-blue-600">
                                Stop {delivery.currentStopIndex} of {delivery.totalStops}
                              </span>
                              {delivery.currentStopStatus === 'ARRIVED' && (
                                <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 bg-green-50 text-green-700 border-green-200">
                                  Arrived
                                </Badge>
                              )}
                            </div>
                          ) : delivery.todayExecutionStatus === 'SCHEDULED' ? (
                            <div className="flex items-center gap-1 text-yellow-600">
                              <Clock className="h-3 w-3" />
                              <span className="text-xs">Scheduled</span>
                            </div>
                          ) : delivery.todayExecutionStatus === 'STARTED' ? (
                            <div className="flex items-center gap-1 text-blue-600">
                              <Truck className="h-3 w-3 animate-pulse" />
                              <span className="text-xs">Started</span>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDelivery(delivery);
                          }}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Activity
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>

        </CardContent>
      </Card>

      {/* Delivery Detail Modal */}
      <DeliveryDetailsModal
        delivery={selectedDelivery}
        open={selectedDelivery !== null}
        onOpenChange={(open) => !open && setSelectedDelivery(null)}
      />
    </div>
  );
}

export default RawMaterialDeliveriesPage;
